import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { useState, type JSX } from "react";

import { RegulatoryPickupAlert } from "@/components/regulatory-documents/regulatory-pickup-alert";
import { RegulatorySignatureAlert } from "@/components/regulatory-documents/regulatory-signature-alert";
import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import {
  isLocationStepComplete,
  isReasonStepComplete,
} from "@/lib/form-schemas/regulatory-document-schema";
import {
  isBindingDeclaration,
  prazoDeAssinatura,
} from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";
import { SolicitarDocumentoRegulatorioMutation } from "@/routes/settings/regulatory-documents/locations";

const listaDeUnidades = (unidades: string[]): string => {
  if (unidades.length <= 1) return unidades.join("");

  return `${unidades.slice(0, -1).join(", ")} e ${unidades.at(-1)}`;
};

const Linha = ({
  rotulo,
  valor,
  detalhe,
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
}): JSX.Element => (
  <div className="border-b border-neutral-50 px-4 py-3 last:border-b-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
      {rotulo}
    </p>
    <p className="text-sm text-neutral-700">{valor}</p>
    {detalhe ? <p className="mt-0.5 text-xs text-neutral-400">{detalhe}</p> : null}
  </div>
);

const RegulatoryDocumentReviewPage = (): JSX.Element => {
  const navigate = useNavigate();
  const draft = useAtomValue(regulatoryDocumentAtom);
  const { user } = Route.useRouteContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: createRequest } = useGraphQLMutationWithErrorHandler(
    SolicitarDocumentoRegulatorioMutation,
  );

  const tipoDePessoa = user.pj ? "Pessoa Jurídica (PJ)" : "Pessoa Física (PF)";
  const unidadesEscolhidas = draft.selectedLocations.map((location) => location.nome);
  const ehRetirada = unidadesEscolhidas.length > 0;
  const prazo = prazoDeAssinatura(draft.codMotivoDocumento, draft.siglaUF);

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await createRequest({
        codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
        codMotivoDocumento: draft.codMotivoDocumento,
        siglaUF: draft.siglaUF,
        codUnidades: ehRetirada
          ? draft.selectedLocations.map((location) => location.codUnidade)
          : null,
        justificativa: draft.requiresJustification ? draft.justification.trim() : null,
      });

      trackEvent("Declaração de Vínculo Solicitada", {
        codMotivoDocumento: draft.codMotivoDocumento,
        siglaUF: draft.siglaUF,
        quantidadeUnidades: draft.coveredLocations.length,
      });

      void navigate({
        to: "/settings/regulatory-documents/confirmation",
        replace: true,
      });
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProgressRoundedLayout progress={1} className="pb-0">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <h1 className="text-xl font-medium leading-tight text-neutral-700">
            Revise sua solicitação
          </h1>

          <div className="rounded-xl border border-neutral-100 bg-white shadow-xs">
            <Linha
              rotulo="Declaração"
              valor={`${draft.documentName} — ${tipoDePessoa}`}
              detalhe="Definido automaticamente pelo seu cadastro"
            />
            <Linha rotulo="Estado" valor={`${draft.stateLabel} (${draft.siglaUF})`} />
            {ehRetirada ? (
              <Linha
                rotulo={
                  unidadesEscolhidas.length > 1
                    ? "Unidades para retirada"
                    : "Unidade para retirada"
                }
                valor={listaDeUnidades(unidadesEscolhidas)}
                detalhe={
                  unidadesEscolhidas.length > 1
                    ? "Sai uma declaração para cada unidade, assinada na própria unidade"
                    : undefined
                }
              />
            ) : (
              <Linha
                rotulo="Unidades contempladas"
                valor={listaDeUnidades(draft.coveredLocations)}
                detalhe="Unidades com o mesmo CNPJ entram uma única vez"
              />
            )}
            <Linha
              rotulo="Motivo"
              valor={draft.reasonLabel}
              detalhe={draft.requiresJustification ? draft.justification : undefined}
            />
            <Linha rotulo="Prazo de assinatura" valor={`Até ${prazo}`} />
          </div>

          {ehRetirada ? (
            <RegulatoryPickupAlert locations={unidadesEscolhidas} prazo={prazo} />
          ) : (
            <RegulatorySignatureAlert
              email={user.email ?? "o e-mail do seu cadastro"}
              prazo={prazo}
            />
          )}
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button fullWidth loading={isSubmitting} onClick={() => void handleSubmit()}>
            {ehRetirada && unidadesEscolhidas.length > 1
              ? "Solicitar declarações"
              : "Solicitar declaração"}
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/review")({
  component: RegulatoryDocumentReviewPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);

    if (
      !isReasonStepComplete(draft) ||
      !isBindingDeclaration(draft.codTipoDocumentoRegulatorio)
    ) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }

    if (!draft.siglaUF) {
      redirect({ to: "/settings/regulatory-documents/state", throw: true });
    }

    if (!isLocationStepComplete(draft)) {
      redirect({
        to: "/settings/regulatory-documents/declaration-locations",
        throw: true,
      });
    }
  },
});
